// form/calendar/AppointmentCalendar.tsx
import { useState, useEffect, useCallback, useRef, useCalendarContext, FullCalendar, dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin, EventInput, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/CalendarImports';
import { useSearchParams } from 'react-router-dom';
import { Appointment } from "models/AppointmentModel";
import { Schedule } from "models/ScheduleModel";
import AsyncSelect from "react-select/async";
import { CalendarHelper } from 'helpers/forms/CalendarHelpers';
import { CalendarManager } from "components/common/CalendarManager";
import tippy from "tippy.js";
import { DateSelectArg } from "@fullcalendar/core";

const AppointmentCalendar = () => {

  const [formId, formName] = [Code.Form.Id.APPOINTMENT, Code.Form.Name.APPOINTMENT];
  const {loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useCalendarContext();
  const [searchParams] = useSearchParams();
  const doctorId = searchParams.get(Code.UrlPara.Doctor);
  const branchId = searchParams.get(Code.UrlPara.Branch);
  const viewType = searchParams.get(Code.UrlPara.CalendarView);
  const sDate = searchParams.get(Code.UrlPara.StartDate);
  const eDate = searchParams.get(Code.UrlPara.EndDate);
  const [appointmentData, setAppointmentData] = useState<Appointment[]>([]); 
  const [scheduleData, setScheduleData] = useState<Schedule[]>([]);
  const [constraintData, setConstraintData] = useState<{ startTime: string; endTime: string } | null>(null);
  const [formData, setFormData] = useState<Appointment>({});
  const [showForm, setShowForm] = useState<boolean>(false);
  const [events, setEvents] = useState<EventInput[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<EventInput>();
  const [typeOptions, setTypeOptions] = Hooks.Form.Options();
  const [statusOptions, setStatusOptions] = Hooks.Form.Options();
  const [postCheckOptions, setPostCheckOptions] = Hooks.Form.Options();
  const [branchOptions, setBranchOptions] = Hooks.Form.Options();
  const [doctorOptions, setDoctorOptions] = Hooks.Form.Options();
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [doctorName, setDoctorName] = useState<string>("");
  //const [examData, setExamData] = useState<Exam[]>([]);
  //const [examLength, setExamLength] = useState<Number>(0);

  const [startTime, setStartTime] = useState<string>("10:00:00");
  const [endTime, setEndTime] = useState<string>("18:00:00");


  const { checkHowClicked } = CalendarHelper.ClickChecker();
  const calendarRef = useRef<FullCalendar>(null);
  const Privilege = Hooks.Privilege.List(formId);
  let isDragging = false;
 
  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  CalendarHelper.DraggableForm(formRef, showForm);

  useEffect(() => { 
    CalendarHelper.setDateRange(viewType, sDate, eDate, calendarRef, setStartDate, setEndDate);
    API.Data.GetDoctorName(doctorId, setDoctorName);
    Files.Json.API(Code.File.OPTION+Code.Group.APPOINTMENT_TYPE, Code.Option.Common.SELECT, setTypeOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.APPOINTMENT_STATUS, Code.Option.Common.SELECT, setStatusOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.APPOINTMENT_INFO, Code.Option.Common.NA, setPostCheckOptions, setError);
    Files.Json.API(Code.File.LOCATION, Code.Option.Common.SELECT, setBranchOptions, setError);
    Files.Json.API(Code.File.DOCTOR, Code.Option.Common.SELECT, setDoctorOptions, setError);
  }, []);
  
 
  const callAppointmentData = useCallback(async () => {
    const start = startDate;
    const end = endDate;
    const url = API.Build.CalendarApiEndpint(formName, {
      fields: Utility.Join.Fields([
        Name.Appointment.Active, Name.Client.Id, Name.Client.FullName, Name.Client.Phone, Name.Doctor.FirstName, 
        Name.Common.BranchName, Name.Client.Dob, Name.Appointment.StartDate, Name.Appointment.EndDate, Name.Common.TypeName,
        Name.Appointment.StartTime, Name.Appointment.EndTime, Name.User.FirstName, Name.Appointment.Comment, 
        Name.Common.StatusName, Name.Common.PostCheckName, Name.Common.StatusColor, Name.Common.GenderId, Name.Appointment.NewOld
      ]),
      doctor_id: doctorId,
      branch_id: branchId,
      appointment_start_date: start,
      appointment_end_date: end,  
    });
    //console.log("Fetching appointments from URL:", url);
    await API.Data.List<Appointment>(url, setAppointmentData, setError, setLoading); 
  },[startDate, endDate]);

  const callScheduleData = useCallback(async () => {
    const start = startDate;
    const end = endDate;
    const url = API.Build.CalendarApiEndpint(Code.Form.Name.SCHEDULE, {
      fields: Utility.Join.Fields([
        Name.Schedule.Id, Name.Doctor.FullName, Name.Common.BranchName, Name.Schedule.StartDate, Name.Schedule.EndDate, 
        Name.Schedule.StartTime, Name.Schedule.EndTime, Name.User.FirstName, Name.Appointment.Comment
      ]),
      doctor_id: doctorId,
      branch_id: branchId,
      schedule_start_date: start,
      schedule_end_date: end,  
    });
    //console.log("Fetching Schedule from URL:", url);
    await API.Data.List<Schedule>(url, setScheduleData, setError, setLoading); 
  },[startDate, endDate]);

  useEffect(() => { 
    if(scheduleData && scheduleData.length>0){
      const firstSchedule = scheduleData?.[0] ?? null;
      if(firstSchedule){
          setStartTime(firstSchedule.schedule_start_time || '10:00:00')
          setEndTime(firstSchedule.schedule_end_time || '18:00:00')
      }
    }
  }, [scheduleData]);

  useEffect(() => { 
    if(doctorId && branchId && startDate && endDate) {
      callAppointmentData();  
      callScheduleData();   
    }
  }, [callAppointmentData, callScheduleData, doctorId, branchId, startDate, endDate]);
  
  useEffect(() => { 
  
    const appointmentEvents: EventInput[] = CalendarHelper.mapAppointmentsToEvent(appointmentData);
    const scheduleBackground: EventInput[] = CalendarHelper.getScheduleToEvent(scheduleData);
    const scheduleConstraint = CalendarHelper.getScheduleConstraint(scheduleData);

    //console.log("Mapped appointment events:", appointmentEvents);
    //console.log("Mapped schedule background events:", scheduleBackground);

    setEvents([...appointmentEvents, ...scheduleBackground]);

    if(scheduleConstraint){
      setConstraintData(scheduleConstraint);
    }
    

  }, [appointmentData, scheduleData]);

  useEffect(() => { 
    if(appointmentData){
      //console.log(appointmentData)
    }
  }, [appointmentData]);

  const onFormSaveClicked = async (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;
    setShowForm(false);
    const keyId = formData.appointment_key? formData.appointment_key : Code.Key.NEW;
    const addonData = await API.Data.AddonData(formData, formName, keyId);
    // if(keyId==Code.Key.NEW){
    //   await callExamList(formData.client_id);
    // }

    const startDate = formData.appointment_start_date || Utility.Date.Current();

    //console.log("startDate~~~~~~~~~~>",startDate);

    const localDate = Utility.Format.LocalDate(startDate);

     //console.log("localDate~~~~~~~~~~>",localDate);

    const finalData = Utility.Sanitize.FormData({
      ...formData,
      ...addonData,
      appointment_start_date: localDate,
      appointment_end_date: localDate,
      appointment_start_time: Utility.Convert.TimeOrNow(formData.appointment_start_time),
      appointment_end_time: Utility.Convert.TimeOrNow(formData.appointment_end_time),
      //newold_id: examLength ? 2 : 3
    });

    //console.log("finalData before save~~~~~~~~~~>",finalData);

    await API.Data.Save(formName, keyId, finalData, setMessage, false, Code.Form.PopupClose.NONE)
    callAppointmentData();
  };

 

  // const callExamList = async (clientId: number | undefined) => {
  //   if (!clientId) return;  
  //   const url = API.Build.FormApiEndpint(Code.Form.Name.EXAM, 0, Name.Exam.Id, Code.Option.Sort.DESC, {
  //     fields: Utility.Join.Fields([
  //       Name.Exam.Id,
  //     ]),
  //     [Name.Client.Id]: clientId,
  //   });
  //   await API.Data.List<Exam>(url, setExamData, setError, setLoading);
  // };

  // useEffect(() => { 
  //   if(examData){
  //     setExamLength(examData.length);
  //   }
  // }, [examData]);

  const onDeleteClick = async () => {
      Toast.Delete.CurrentRecord(Code.Option.Active.ACTIVE, setConfirm);
  };

  const DeleteAppointment = async () => {
    if(selectedEvent) {
      await API.Data.Delete(formName, selectedEvent.event.id, Code.Option.Active.ACTIVE, setMessage, Code.Form.PopupClose.NONE); 
      callAppointmentData();  
      setShowForm(false);   
    }
  }

  const handleEventSingleClick = (eventInfo: EventInput) => {
    const { isCtrl, isAlt } = Utility.Check.KeyPressed(eventInfo);
    const selected = eventInfo;
    if(selected){
      setSelectedEvent(selected);
      if(isCtrl || isAlt) {
        openAppointmentForModify(selected);
      } 
    }
  };

  const handleEventDoubleClick = (eventInfo: any) => {
    Utility.Handle.openClientOnPanel(eventInfo.event.title, eventInfo.event.extendedProps.clientId);
  };

  const openAppointmentForNew = (eventInfo: DateSelectArg | undefined) => {
    if(eventInfo){
      var startDate = Utility.Format.Date(eventInfo.start);
      var startTime = Utility.Format.Date(eventInfo.start, Code.Format.DateTime.TimeSS);
      var endTime = Utility.Format.Date(eventInfo.end, Code.Format.DateTime.TimeSS);
      var doctorId = searchParams.get(Code.UrlPara.Doctor);
      var branchId= searchParams.get(Code.UrlPara.Branch);
      Form.Show.Popup(iframeRef, Code.Form.Name.APPOINTMENT, Code.UrlPara.Doctor, doctorId?.toString(), Code.UrlPara.Branch, branchId?.toString(), Code.UrlPara.Date, startDate, Code.UrlPara.StartTime, startTime, Code.UrlPara.EndTime, endTime)
    }
  };
  
  const openAppointmentForModify = (selectedEvent: EventInput | undefined) => {
    if(selectedEvent){
      console.log("openClientFormOnLocal:", selectedEvent.event.id);
      Form.Show.Popup(iframeRef, Utility.Join.Edit(Privilege, Code.Form.Name.APPOINTMENT, selectedEvent.event.id))
    }
    //CalendarHelper.openClientFormOnPopup(selectedEvent, setShowForm, setFormData, setLoading, setError)
  };

  const handleStatusChange = async (statusId: number) => {
    if (selectedEvent) {
      const keyId = selectedEvent.event.id;
      const finalData = {
        appointment_key: keyId,
        status_id: statusId,
      };
      await API.Data.Save(formName, keyId, finalData, setMessage, true, Code.Form.PopupClose.NONE);
      Utility.Message.Warning(["Appointment status updated."],setMessage);
      callAppointmentData();
    }
  };

  const handlePostCheckChange = async (postCheckId: number) => {
    if (selectedEvent) {
      const keyId = selectedEvent.event.id;
      const finalData = {
        appointment_key: keyId,
        postcheck_id: postCheckId,
      };
      await API.Data.Save(formName, keyId, finalData, setMessage, true, Code.Form.PopupClose.NONE);
      Utility.Message.Warning(["Post-check status updated."], setMessage);
      callAppointmentData();
    }
  };

  const callAppointmentDataRef = useRef(callAppointmentData);
  useEffect(() => {
    callAppointmentDataRef.current = callAppointmentData;
  }, [callAppointmentData]);

  const onRefresh = useCallback(() => {
    callAppointmentDataRef.current();
  }, []);
  
  return (
    <>
      <Control.Form.CalendarFrame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
        <Messages.Toast Messages={message} Error={error} />
        <Messages.Confirm Messages={confirm} onConfirm={() => DeleteAppointment()} onRefuse={()=>{}} />
        <FullCalendar
          ref={calendarRef}
          allDaySlot={false}
          selectable={true}
          editable={true}
          weekends={true}
          events={events}
          slotMinTime={startTime || "10:00:00"}
          slotMaxTime={endTime || "18:00:00"}
          slotDuration="00:05:00"
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
          initialView={sDate ? Code.Calendar.View.DayGrid : Code.Calendar.View.WeekGrid}
          {...(sDate ? { initialDate: sDate } : {})}
          headerToolbar={{ left: "title filter", center: "", right: `${Code.Calendar.View.DayGrid},${Code.Calendar.View.WeekGrid},${Code.Calendar.View.MonthGrid},${Code.Calendar.View.WeekList},prev,next`}}
          customButtons={{ filter:{ text: doctorName || '' }}}
          selectConstraint={constraintData || undefined}
          eventConstraint={constraintData || undefined}
          eventContent={(arg) => <CalendarManager.Event.Content arg={arg} />}
          select={(eventInfo) => openAppointmentForNew(eventInfo)}
          //select={(eventInfo) => CalendarHelper.handleEventAdd(eventInfo, setShowForm, setFormData, setSelectedEvent, doctorId, branchId)}
          eventClick={async (eventInfo) => { 
            const isDoubleClick = await checkHowClicked();
            if (isDoubleClick) { handleEventDoubleClick(eventInfo); } else { handleEventSingleClick(eventInfo); }
          }}
          eventChange={(eventInfo) => CalendarHelper.handleEventChange(eventInfo, setMessage)}
          eventDragStart={() => { isDragging = true; }}
          eventDragStop={() => { isDragging = false; }}   
          eventDidMount={(info) => { 
            info.el.style.backgroundColor = info.event.extendedProps.statusColor;
            if (!isDragging) {
              tippy(info.el, { 
                content: info.event.extendedProps.info || info.event.title, 
                allowHTML: true, 
                delay: [500, 0], 
              }) 
            }
          }}
          datesSet={(arg) => { CalendarHelper.ChangeDate(arg, setStartDate, setEndDate); }}
          eventClassNames={(arg) => {
            return arg.event.id === selectedEvent?.event.id ? ['fc-selected-event'] : [];
          }}
        />
      </Control.Form.CalendarFrame>
      {showForm && (
        <Control.Form.CalendarForm setShowForm={setShowForm} formRef={formRef}> 
          <Control.Box>
            <Field.Header id={Name.Client.Id} title={Title.Client} require={true}>
              <AsyncSelect id={Name.Client.Id} loadOptions={(inputValue) => API.Data.ClientAsyncOption(inputValue, Code.Form.Name.CLIENT, Code.Mapping.client.value, Code.Mapping.client.label)} styles={Code.Style.SearchOption} onChange={(selected) => Utility.Handle.ClientChange(selected, setFormData)} value={Utility.Value.Async(formData.client_id,formData.client_name)} placeholder="Search" />
            </Field.Header>
          </Control.Box>
          <Control.Row>
            <Field.Select id={Name.Common.TypeId} value={formData.type_id} title={Title.Type} options={typeOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} />
            <Field.Date id={Name.Appointment.StartDate} value={formData.appointment_start_date} title={Title.Date} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]}  require={true} currentDate={true}  />
          </Control.Row>
          <Control.Row>
            <Field.Select id={Name.Doctor.Id} value={formData.doctor_id} title={Title.Doctor} options={doctorOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} selected={doctorId} />
            <Field.Select id={Name.Common.BranchId} value={formData.branch_id} title={Title.Location} options={branchOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} require={true} selected={branchId} />
          </Control.Row>
          <Control.Row>
            <Field.Time id={Name.Appointment.StartTime} value={formData.appointment_start_time} title={Title.StartTime} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} currentTime={true} />
            <Field.Time id={Name.Appointment.EndTime} value={formData.appointment_end_time} title={Title.EndTime} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} require={true}  currentTime={true} />
          </Control.Row>
          <Control.Box>
            <Field.Area id={Name.Appointment.Comment} value={formData.appointment_comment} title={Title.Comment} onChange={(e) => Form.Field.Change(e, formData, setFormData)} height={formData.appointment_comment ? 128 : 80} />
          </Control.Box>
          <Control.Row>
            <Field.Select id={Name.Common.StatusId} value={formData.status_id} title={Title.Status} options={statusOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} />
            <Field.Select id={Name.Common.PostCheckId} value={formData.postcheck_id} title="Post Check Status" options={postCheckOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]}  />
          </Control.Row>
          <Control.ButtonColumn size={[40,2,0,0,0]}>
            <Button.Action title="Save" onClick={() => onFormSaveClicked(new Event('submit') as unknown as React.FormEvent)} />
            <Button.Delete onClick={() => onDeleteClick()} activeId={2} Privilege={Privilege} />
          </Control.ButtonColumn>
        </Control.Form.CalendarForm>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.CalendarContextMenu selectedEvent={selectedEvent} showMenu={showMenu} formName={formName} statusOptions={statusOptions} handleStatusChange={handleStatusChange} handlePostCheckChange={handlePostCheckChange} postCheckOptions={postCheckOptions} openLocalForm={() => openAppointmentForModify(selectedEvent)} handleDelete={() => onDeleteClick()} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Control.FormPopup iframeRef={iframeRef} onRefresh={onRefresh} />
      <Actions.Calendar />
    </>
  );
};

export default AppointmentCalendar;